(() => {
  if (window.crosightShowcaseCardsInstalled) return;
  window.crosightShowcaseCardsInstalled = true;
  const initialize = sectionRoot => {
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const fallbackDefaultKeyword = '650';

    const formatMoney = (cents, symbol) => {
      const amount = Number(cents || 0) / 100;
      const formattedAmount = `${symbol || '$'}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return `<span class="langwill-money">${formattedAmount}</span>`;
    };

    const refreshCurrencyConverters = () => {
      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('theme:price:update'));
        document.dispatchEvent(new CustomEvent('currency:refresh'));
        window.dispatchEvent(new CustomEvent('theme:price:update'));

        ['Langwill', 'langwill', 'LWCurrency', 'Currency'].forEach((globalName) => {
          const api = window[globalName];
          if (!api) return;

          ['refresh', 'update', 'convert', 'convertAll', 'reload', 'init'].forEach((methodName) => {
            if (typeof api[methodName] !== 'function') return;

            try {
              api[methodName]();
            } catch (error) {
              // Some currency apps require arguments for legacy helpers. The DOM event above is the primary hook.
            }
          });
        });
      });
    };

    const isPurchaseable = (variant) => {
      return Boolean(variant && (variant.available || variant.inventory_policy === 'continue'));
    };

    const getVariantOption = (variant, position) => {
      return variant ? variant[`option${position}`] : '';
    };

    const getOptionControls = (card) => {
      return Array.from(card.querySelectorAll('[data-option-position][data-variant-option-value]'));
    };

    const parseVariants = (card) => {
      const variantsJsonEl = card.querySelector('.js-product-variants-json');
      try {
        const variants = variantsJsonEl ? JSON.parse(variantsJsonEl.textContent || '[]') : [];
        const ids = new Set((card.dataset.filteredVariantIds || '').split(',').filter(Boolean));
        return ids.size ? variants.filter(variant => ids.has(String(variant.id))) : variants;
      } catch (error) {
        return [];
      }
    };

    const getAllOptionPositions = (card) => {
      return Array.from(new Set(
        getOptionControls(card)
          .map((control) => String(control.dataset.optionPosition || ''))
          .filter(Boolean)
      ));
    };

    const getControlsByPosition = (card, position) => {
      return getOptionControls(card).filter((control) => String(control.dataset.optionPosition) === String(position));
    };

    const setActiveOption = (card, position, value) => {
      const controls = getControlsByPosition(card, position);
      if (controls.length === 0) return;

      let matchedAny = false;
      controls.forEach((control) => {
        const matched = normalize(control.dataset.variantOptionValue) === normalize(value);
        control.classList.toggle('is-active', matched);
        if (matched) matchedAny = true;
      });

      if (!matchedAny) {
        controls.forEach((control) => control.classList.remove('is-active'));
        controls[0].classList.add('is-active');
      }
    };

    const initControlsFromVariant = (card, variant) => {
      getAllOptionPositions(card).forEach((position) => {
        setActiveOption(card, position, getVariantOption(variant, position));
      });
    };

    const getSelectedByPosition = (card) => {
      const selected = {};
      getOptionControls(card).forEach((control) => {
        if (!control.classList.contains('is-active')) return;
        const position = String(control.dataset.optionPosition || '');
        if (position) selected[position] = control.dataset.variantOptionValue || '';
      });
      return selected;
    };

    const variantMatchesSelections = (variant, selections) => {
      return Object.entries(selections).every(([position, value]) => {
        return normalize(getVariantOption(variant, position)) === normalize(value);
      });
    };

    const findVariantBySelections = (variants, selections) => {
      return variants.find((variant) => variantMatchesSelections(variant, selections)) || null;
    };

    const findCandidateVariant = (card, variants, changedPosition, candidateValue) => {
      const selections = getSelectedByPosition(card);
      selections[String(changedPosition)] = candidateValue || '';
      return findVariantBySelections(variants, selections);
    };

    const getPreferredDefaultKeyword = (card) => {
      return normalize(card.dataset.defaultVariantKeyword || fallbackDefaultKeyword);
    };

    const variantContainsPreferredKeyword = (variant, preferredKeyword) => {
      if (!preferredKeyword || !variant || !Array.isArray(variant.options)) return false;
      return variant.options.some((optionValue) => normalize(optionValue).includes(preferredKeyword));
    };

    const findDefaultVariant = (card, variants, defaultVariantId) => {
      const preferredDefaultKeyword = getPreferredDefaultKeyword(card);
      const defaultOptionPosition = card.dataset.defaultOptionPosition;
      const defaultOptionValue = card.dataset.defaultOptionValue;
      const optionMatchedBuyableVariant = variants.find((variant) => {
        return defaultOptionPosition &&
          normalize(getVariantOption(variant, defaultOptionPosition)) === normalize(defaultOptionValue) &&
          variantContainsPreferredKeyword(variant, preferredDefaultKeyword) &&
          isPurchaseable(variant);
      });
      const optionMatchedVariant = variants.find((variant) => {
        return defaultOptionPosition &&
          normalize(getVariantOption(variant, defaultOptionPosition)) === normalize(defaultOptionValue) &&
          variantContainsPreferredKeyword(variant, preferredDefaultKeyword);
      });
      const optionMatchedFirstBuyableVariant = variants.find((variant) => {
        return defaultOptionPosition &&
          normalize(getVariantOption(variant, defaultOptionPosition)) === normalize(defaultOptionValue) &&
          isPurchaseable(variant);
      });
      const optionMatchedFirstVariant = variants.find((variant) => {
        return defaultOptionPosition &&
          normalize(getVariantOption(variant, defaultOptionPosition)) === normalize(defaultOptionValue);
      });
      const idMatchedVariant = variants.find((variant) => String(variant.id) === String(defaultVariantId));
      const preferredBuyableVariant = variants.find((variant) => variantContainsPreferredKeyword(variant, preferredDefaultKeyword) && isPurchaseable(variant));
      const preferredVariant = variants.find((variant) => variantContainsPreferredKeyword(variant, preferredDefaultKeyword));
      const firstBuyableVariant = variants.find((variant) => isPurchaseable(variant));

      return optionMatchedBuyableVariant || optionMatchedFirstBuyableVariant || optionMatchedVariant || optionMatchedFirstVariant || preferredBuyableVariant || preferredVariant || firstBuyableVariant || idMatchedVariant || variants[0];
    };

    const refreshOptionAvailability = (card, variants) => {
      getOptionControls(card).forEach((control) => {
        const candidateVariant = findCandidateVariant(
          card,
          variants,
          control.dataset.optionPosition,
          control.dataset.variantOptionValue
        );

        control.classList.toggle('is-soldout', !isPurchaseable(candidateVariant));
      });
    };

    const getVariantPowerKey = (matchedVariant) => {
      if (!matchedVariant) return 'default';

      const variantText = [
        matchedVariant.title,
        matchedVariant.public_title,
        matchedVariant.name,
        matchedVariant.sku,
        matchedVariant.option1,
        matchedVariant.option2,
        matchedVariant.option3,
        ...(Array.isArray(matchedVariant.options) ? matchedVariant.options : [])
      ].filter(Boolean).join(' ');

      return normalize(variantText).includes('650') ? '650' : 'default';
    };

    const updateParamsByVariant = (card, matchedVariant) => {
      const paramBox = card.querySelector('.custom-icon-params');
      if (!paramBox) return;

      const powerKey = getVariantPowerKey(matchedVariant);
      const preassembledEl = card.querySelector('.js-param-preassembled');
      const rangeEl = card.querySelector('.js-param-range');
      const peakEl = card.querySelector('.js-param-peak');
      const batteryEl = card.querySelector('.js-param-battery');
      const getParamValue = (name) => {
        const attributeName = powerKey === '650' ? `data-param-650-${name}` : `data-param-default-${name}`;
        return paramBox.getAttribute(attributeName) || paramBox.getAttribute(`data-param-default-${name}`);
      };

      const values = {
        preassembled: paramBox.getAttribute('data-param-preassembled') || (preassembledEl ? preassembledEl.textContent : ''),
        range: getParamValue('range') || (rangeEl ? rangeEl.textContent : ''),
        peak: getParamValue('peak') || (peakEl ? peakEl.textContent : ''),
        battery: getParamValue('battery') || (batteryEl ? batteryEl.textContent : '')
      };

      if (preassembledEl) preassembledEl.textContent = values.preassembled;
      if (rangeEl) rangeEl.textContent = values.range;
      if (peakEl) peakEl.textContent = values.peak;
      if (batteryEl) batteryEl.textContent = values.battery;
    };

    const getColorSwatches = (card) => {
      return Array.from(card.querySelectorAll('.custom-swatch-dot[data-option-role="color"][data-option-position][data-variant-option-value]'));
    };

    const getActiveColorSwatch = (card) => {
      return card.querySelector('.custom-swatch-dot.is-active[data-option-role="color"][data-option-position][data-variant-option-value]');
    };

    const getActiveSwatchImage = (card) => {
      const activeSwatch = getActiveColorSwatch(card);
      return activeSwatch ? activeSwatch.dataset.variantImage || '' : '';
    };

    const updateColorCardCopy = (card) => {
      if (card.dataset.colorCardTitle !== 'true') return;

      const activeSwatch = getActiveColorSwatch(card);
      const colorValue = activeSwatch ? activeSwatch.dataset.variantOptionValue || activeSwatch.dataset.colorValue || '' : '';
      const titleEl = card.querySelector('.custom-product-title');
      const baseTitle = card.dataset.productTitle || (titleEl ? titleEl.textContent : '');

      if (titleEl && baseTitle) {
        const shouldAppendColor = colorValue && !normalize(baseTitle).includes(normalize(colorValue));
        titleEl.textContent = shouldAppendColor ? `${baseTitle} ${colorValue}` : baseTitle;
      }

      const descEl = card.querySelector('.custom-product-desc');
      if (!descEl || !activeSwatch) return;

      const swatches = getColorSwatches(card);
      const activeIndex = Math.max(0, swatches.indexOf(activeSwatch));
      const descByIndex = [
        card.dataset.colorDescOne,
        card.dataset.colorDescTwo,
        card.dataset.colorDescThree,
        card.dataset.colorDescFour
      ];
      const descText = descByIndex[activeIndex] || descByIndex.find(Boolean) || descEl.textContent;
      if (descText) descEl.textContent = descText;
    };

    const setCardButtonText = (button, value, available) => {
      const label = button.querySelector('[data-crosight-purchase-label]');
      if (label) label.textContent = value;
      else button.textContent = value;
      const countdown = button.querySelector('[data-crosight-inline-countdown]');
      if (countdown) countdown.dataset.crosightVariantAvailable = String(available === true);
      window.crosightCountdownCore?.tick();
    };

    const updateCardUI = (card, matchedVariant, variants) => {
      const btnEl = card.querySelector('.js-card-btn');
      const homeCountdown = card.querySelector('[data-crosight-home-countdown]');
      const canadaCountdown = card.querySelector('[data-crosight-ca-sale]');
      const statusBadgeContainer = card.querySelector('.js-badge-status-container');
      const inlineSaveEl = card.querySelector('.js-price-save-inline');
      const salePriceEl = card.querySelector('.js-card-sale-price');
      const comparePriceEl = card.querySelector('.js-card-compare-price');
      const symbol = card.dataset.moneySymbol || '$';

      refreshOptionAvailability(card, variants);
      updateParamsByVariant(card, matchedVariant);
      updateColorCardCopy(card);

      const promotionStock = matchedVariant?.available === true && (card.dataset.crosightPromotionStockIds || '').split(',').includes(String(matchedVariant.id));
      if (!matchedVariant) {
        if (homeCountdown) { homeCountdown.hidden = true; homeCountdown.dataset.crosightVariantAvailable = 'false'; }
        if (canadaCountdown) canadaCountdown.dataset.crosightVariantAvailable = 'false';
        window.crosightCountdownCore?.tick();
        if (btnEl) {
          setCardButtonText(btnEl, card.dataset.crosightUnavailableLabel || 'UNAVAILABLE', false);
          btnEl.classList.add('btn-out-of-stock');
          btnEl.removeAttribute('href');
        }
        if (statusBadgeContainer) {
          statusBadgeContainer.innerHTML = '<div class="custom-badge-base badge-soldout-black">UNAVAILABLE</div>';
        }
        if (comparePriceEl) comparePriceEl.style.display = 'none';
        if (inlineSaveEl) inlineSaveEl.style.display = 'none';
        return;
      }

      const canBuy = isPurchaseable(matchedVariant);
      if (homeCountdown) {
        homeCountdown.dataset.crosightVariantAvailable = String(promotionStock);
        homeCountdown.hidden = !promotionStock;
      }
      if (canadaCountdown) canadaCountdown.dataset.crosightVariantAvailable = String(promotionStock);
      window.crosightCountdownCore?.tick();
      const comparePrice = Number(matchedVariant.compare_at_price || 0);
      const price = Number(matchedVariant.price || 0);
      const isOnSale = canBuy && comparePrice > price;

      if (btnEl) {
        if (canBuy) {
          setCardButtonText(btnEl, card.dataset.crosightBuyLabel || 'SHOP NOW', promotionStock);
          btnEl.classList.remove('btn-out-of-stock');
        } else {
          setCardButtonText(btnEl, card.dataset.crosightSoldLabel || 'OUT OF STOCK', false);
          btnEl.classList.add('btn-out-of-stock');
        }

        const productUrl = card.dataset.productUrl || '';
        if (matchedVariant.id && productUrl) {
          const targetUrl = new URL(productUrl, window.location.origin);
          targetUrl.searchParams.set('variant', String(matchedVariant.id));
          btnEl.href = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
          card.querySelectorAll('.custom-product-media > a, .custom-product-title').forEach(link => { link.href = btnEl.href; });
        }
      }

      if (statusBadgeContainer) {
        if (!canBuy) {
          statusBadgeContainer.innerHTML = '<div class="custom-badge-base badge-soldout-black">SOLD OUT</div>';
        } else if (isOnSale) {
          statusBadgeContainer.innerHTML = '<div class="custom-badge-base badge-sale-red">ON SALE</div>';
        } else {
          statusBadgeContainer.innerHTML = '';
        }
      }

      if (salePriceEl) {
        salePriceEl.innerHTML = formatMoney(price, symbol);
      }

      if (comparePriceEl) {
        if (isOnSale) {
          comparePriceEl.innerHTML = formatMoney(comparePrice, symbol);
          comparePriceEl.style.display = 'inline';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      if (inlineSaveEl) {
        if (isOnSale) {
          inlineSaveEl.innerHTML = `Save <span class="money">${formatMoney(comparePrice - price, symbol)}</span>`;
          inlineSaveEl.style.display = 'inline-flex';
        } else {
          inlineSaveEl.style.display = 'none';
        }
      }

      refreshCurrencyConverters();

      const imgEl = card.querySelector('.product-card-image');
      const activeSwatchImage = getActiveSwatchImage(card);
      const matchedVariantImage = matchedVariant.featured_image && matchedVariant.featured_image.src ? matchedVariant.featured_image.src : '';

      if (imgEl && matchedVariantImage && !matchedVariantImage.includes('no-image')) {
        imgEl.src = matchedVariantImage;
      } else if (imgEl && activeSwatchImage && !activeSwatchImage.includes('no-image')) {
        imgEl.src = activeSwatchImage;
      }
    };

    sectionRoot.querySelectorAll('.featured-collection-tabs__panel').forEach((panel) => {
      if (panel.dataset.cardsExpanded) return;
      panel.dataset.cardsExpanded = 'true';
      const tabLabel = normalize(panel.dataset.tabLabel);
      const collectionHandle = normalize(panel.dataset.collectionHandle);
      const isT3Panel = tabLabel.includes('3.0') || tabLabel.includes('t3') || collectionHandle.includes('3-0') || collectionHandle.includes('t3');
      const isTrailRiderPanel = tabLabel.includes('2.0') || tabLabel.includes('2-0') || collectionHandle.includes('2-0') || collectionHandle.includes('trail-rider');
      if (!isT3Panel && !isTrailRiderPanel) return;

      const productList = panel.querySelector('.product-list');
      if (!productList) return;

      const existingCards = Array.from(productList.querySelectorAll(':scope > .custom-product-card'));
      if (existingCards.length !== 1) return;

      const firstCard = existingCards[0];
      const seenColors = new Set();
      let colorSwatches = getColorSwatches(firstCard).filter((swatch) => {
        const colorKey = normalize(swatch.dataset.variantOptionValue);
        if (!colorKey || seenColors.has(colorKey)) return false;
        seenColors.add(colorKey);
        return true;
      });

      const isStandardT3Panel = (tabLabel.includes('t3') && !tabLabel.includes('pro')) || collectionHandle.includes('t3-refined');
      if (isStandardT3Panel) {
        const colorOrder = ['white', 'orange', 'black', 'purple'];
        colorSwatches = colorSwatches.sort((a, b) => {
          const aIndex = colorOrder.indexOf(normalize(a.dataset.variantOptionValue));
          const bIndex = colorOrder.indexOf(normalize(b.dataset.variantOptionValue));
          const safeA = aIndex === -1 ? colorOrder.length : aIndex;
          const safeB = bIndex === -1 ? colorOrder.length : bIndex;
          return safeA - safeB;
        });
      } else if (isTrailRiderPanel) {
        const colorOrder = ['black', 'purple', 'orange', 'white'];
        colorSwatches = colorSwatches.sort((a, b) => {
          const aValue = normalize(a.dataset.variantOptionValue);
          const bValue = normalize(b.dataset.variantOptionValue);
          const aIndex = colorOrder.findIndex((color) => aValue.includes(color));
          const bIndex = colorOrder.findIndex((color) => bValue.includes(color));
          const safeA = aIndex === -1 ? colorOrder.length : aIndex;
          const safeB = bIndex === -1 ? colorOrder.length : bIndex;
          return safeA - safeB;
        });
      }

      const variants = parseVariants(firstCard);
      colorSwatches = colorSwatches.map((swatch, index) => ({
        swatch,
        index,
        available: variants.some((variant) =>
          normalize(getVariantOption(variant, swatch.dataset.optionPosition)) === normalize(swatch.dataset.variantOptionValue) &&
          isPurchaseable(variant)
        ),
        randomOrder: Math.random()
      })).sort((a, b) =>
        Number(b.available) - Number(a.available) ||
        (a.available ? a.randomOrder - b.randomOrder : a.index - b.index)
      ).slice(0, 3).map(({ swatch }) => swatch);
      if (colorSwatches.length < 2) return;

      firstCard.dataset.colorCardTitle = 'true';
      firstCard.dataset.defaultOptionPosition = colorSwatches[0].dataset.optionPosition || '';
      firstCard.dataset.defaultOptionValue = colorSwatches[0].dataset.variantOptionValue || '';

      colorSwatches.slice(1).forEach((swatch, index) => {
        const clonedCard = firstCard.cloneNode(true);
        const hoverPosition = index + 2;
        const configuredHoverSrc = firstCard.dataset[`hoverImage${hoverPosition}`] || '';
        const mediaLink = clonedCard.querySelector('.custom-product-media > a');
        let hoverImage = clonedCard.querySelector('.product-card-hover-image');

        if (configuredHoverSrc && mediaLink) {
          if (!hoverImage) {
            hoverImage = document.createElement('img');
            hoverImage.className = 'product-card-hover-image';
            hoverImage.loading = 'lazy';
            hoverImage.width = 1000;
            hoverImage.height = 1000;
            hoverImage.alt = `${firstCard.dataset.productTitle || 'Product'} alternate view`;
            mediaLink.appendChild(hoverImage);
          }

          hoverImage.removeAttribute('srcset');
          hoverImage.removeAttribute('sizes');
          hoverImage.src = configuredHoverSrc;
          clonedCard.classList.add('has-custom-hover-image');
        } else {
          hoverImage?.remove();
          clonedCard.classList.remove('has-custom-hover-image');
        }

        clonedCard.dataset.productId = `${firstCard.dataset.productId || 'product'}-${index + 2}`;
        clonedCard.dataset.colorCardTitle = 'true';
        clonedCard.dataset.defaultOptionPosition = swatch.dataset.optionPosition || '';
        clonedCard.dataset.defaultOptionValue = swatch.dataset.variantOptionValue || '';
        productList.appendChild(clonedCard);
      });
    });

    sectionRoot.querySelectorAll('.custom-product-card:not([data-showcase-initialized])').forEach((card) => {
      card.dataset.showcaseInitialized = 'true';
      const variants = parseVariants(card);
      if (!variants.length) return;

      const defaultVariantId = String(card.dataset.defaultVariantId || '');
      const defaultVariant = findDefaultVariant(card, variants, defaultVariantId);

      const syncVariantStatus = () => {
        const selections = getSelectedByPosition(card);
        const matchedVariant = findVariantBySelections(variants, selections);
        updateCardUI(card, matchedVariant, variants);
      };

      initControlsFromVariant(card, defaultVariant);
      getOptionControls(card).forEach(control => {
        control.setAttribute('role', 'button');
        control.tabIndex = 0;
        control.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); control.click(); }
        });
      });

      getOptionControls(card).forEach((control) => {
        control.addEventListener('click', function(event) {
          event.preventDefault();
          const next = findCandidateVariant(card, variants, this.dataset.optionPosition, this.dataset.variantOptionValue);
          if (!next) return;
          setActiveOption(card, this.dataset.optionPosition, this.dataset.variantOptionValue);
          syncVariantStatus();
        });
      });

      syncVariantStatus();
    });

  };
  const initializePending = () => {
    const roots = new Set(Array.from(document.querySelectorAll('.custom-product-card:not([data-showcase-initialized])')).map(card => card.closest('.shopify-section')).filter(Boolean));
    roots.forEach(initialize);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializePending, {once:true});
  else initializePending();
  document.addEventListener('shopify:section:load', initializePending);
  let queued = false;
  new MutationObserver(records => {
    if (queued || !records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('.custom-product-card') || node.querySelector?.('.custom-product-card:not([data-showcase-initialized])'))))) return;
    queued = true;
    queueMicrotask(() => { queued = false; initializePending(); });
  }).observe(document.documentElement, {childList:true,subtree:true});
})();



(() => {
  if (window.crosightCountdownCore) {
    window.crosightCountdownCore.tick();
    return;
  }

  const isDisplaySlot = document.body.dataset.crosightDisplaySlot === 'true';
  if (document.body.dataset.crosightCountdownEnabled !== 'true' && document.body.dataset.crosightInlineCountdownEnabled !== 'true') return;
  const pad = value => String(value).padStart(2, '0');
  const clock = seconds => {
    const value = Math.max(0, seconds);
    return `${pad(Math.floor(value / 3600))}:${pad(Math.floor(value % 3600 / 60))}:${pad(value % 60)}`;
  };
  const cycleStart = Date.parse(`${document.body.dataset.crosightBadgeAnchor}${document.body.dataset.crosightBadgeTimezone}`);
  const cycleSeconds = Number(document.body.dataset.crosightBadgeHours) * 60 * 60;
  const getTimeState = (now = Date.now()) => {
    if (document.body.dataset.crosightBadgeRule === 'schedule') return getInlineTimeState(now);
    if (!Number.isFinite(cycleStart) || !Number.isFinite(cycleSeconds) || cycleSeconds <= 0) return { active: false, seconds: 0 };
    const elapsedSeconds = Math.floor((now - cycleStart) / 1000);
    const cyclePosition = ((elapsedSeconds % cycleSeconds) + cycleSeconds) % cycleSeconds;
    return { active: true, seconds: cycleSeconds - cyclePosition };
  };
  const getInlineTimeState = (now = Date.now()) => {
    const { crosightInlineStart: startText, crosightInlineEnd: endText, crosightInlineTimezone: offset, crosightInlineLoop: loopText, crosightInlineLoopHours: hoursText } = document.body.dataset;
    if (!/^[+-](?:0\d|1[0-4]):[0-5]\d$/.test(offset || '') || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(startText || '') || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(endText || '')) return { active: false, seconds: 0 };
    const start = Date.parse(`${startText}${offset}`);
    const end = Date.parse(`${endText}${offset}`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || now < start) return { active: false, seconds: 0 };
    if (now < end) return { active: true, seconds: Math.ceil((end - now) / 1000) };
    if (loopText !== 'true') return { active: false, seconds: 0 };
    const duration = Number(hoursText) * 60 * 60 * 1000;
    if (!Number.isFinite(duration) || duration <= 0) return { active: false, seconds: 0 };
    const elapsed = ((now - end) % duration + duration) % duration;
    return { active: true, seconds: Math.ceil((duration - elapsed) / 1000) };
  };
  const getDialog = () => document.querySelector('[data-crosight-ca-sale-dialog]');
  const variantAvailable = element => element.dataset.crosightVariantAvailable === 'true';
  const controller = {
    getTimeState,
    tick() {
      const dialog = getDialog();
      if (isDisplaySlot) {
        if (!dialog) return;
        const end = Date.parse(dialog.dataset.end || '');
        const cycle = 86400000;
        const remaining = ((end - Date.now()) % cycle + cycle) % cycle;
        const active = Number.isFinite(end);
        const seconds = Math.max(0, Math.floor(remaining / 1000));
        const text = `${Math.floor(seconds / 86400)}d ${clock(seconds % 86400)}`;
        document.querySelectorAll('[data-crosight-ca-sale-timer]').forEach(timer => { timer.textContent = text; });
        document.querySelectorAll('[data-crosight-ca-sale]').forEach(pill => { pill.hidden = (!active && dialog.dataset.designMode !== 'true') || !variantAvailable(pill); });
        if ((!active || !document.querySelector('[data-crosight-promotion-badge]:not([hidden])')) && dialog.open) dialog.close();
        return;
      }

      const inline = getInlineTimeState();
      const inlineEditorPreview = document.body.dataset.crosightInlineEditorPreview === 'true';
      document.querySelectorAll('[data-crosight-inline-countdown]').forEach(element => {
        const visible = (inline.active || inlineEditorPreview) && variantAvailable(element);
        element.hidden = !visible;
        element.closest('.custom-shop-now-btn, .crosight-buy-now')?.classList.toggle('crosight-inline-active', visible);
        const time = element.querySelector('[data-crosight-inline-time]');
        if (time && visible) time.textContent = clock(inline.seconds);
      });

      const { active, seconds } = getTimeState();
      const timeText = clock(seconds);
      document.querySelectorAll('[data-crosight-home-countdown]').forEach(pill => {
        pill.hidden = !active || !variantAvailable(pill);
      });
      document.querySelectorAll('[data-crosight-shared-countdown]').forEach(timer => {
        timer.textContent = timeText;
        timer.hidden = !active;
      });
      document.querySelectorAll('[data-crosight-product-countdown]').forEach(cta => {
        cta.hidden = !active || !variantAvailable(cta);
        if (cta.hidden) return;
        const values = [pad(0), pad(Math.floor(seconds / 3600)), pad(Math.floor(seconds % 3600 / 60)), pad(seconds % 60)];
        ['days', 'hours', 'minutes', 'seconds'].forEach((unit, index) => {
          const field = cta.querySelector(`[data-countdown-${unit}]`);
          if (field) field.textContent = values[index];
        });
      });

      document.querySelectorAll('[data-crosight-promotion-badge]').forEach(badge => {
        badge.hidden = !active || !variantAvailable(badge);
      });
      const promoText = `0d ${timeText}`;
      document.querySelectorAll('[data-crosight-ca-sale-timer]').forEach(timer => { timer.textContent = promoText; });
      document.querySelectorAll('[data-crosight-ca-sale]').forEach(pill => {
        pill.hidden = !active || !variantAvailable(pill);
      });
      if (dialog) {
        if ((!active || !document.querySelector('[data-crosight-promotion-badge]:not([hidden])')) && dialog.open) dialog.close();
      }
    }
  };

  document.addEventListener('click', async event => {
    const action = event.target.closest('[data-crosight-promotion-action]');
    if (action?.dataset.crosightPromotionAction === 'close') { getDialog()?.close(); return; }
    const subscribe = action?.dataset.crosightPromotionAction === 'newsletter' ? action : null;
    if (subscribe) {
      const trigger = document.querySelector('[data-newsletter-tab][aria-controls="newsletter-drawer"]');
      const popup = document.getElementById('newsletter-drawer');
      if (trigger && popup) {
        await customElements.whenDefined(popup.localName);
        const dialog = getDialog();
        if (dialog?.open) dialog.close();
        if (!popup.hasAttribute('open')) trigger.click();
      } else {
        const email = document.querySelector('[id^="newsletter-form-"] input[type="email"]');
        if (email) {
          getDialog()?.close();
          email.scrollIntoView({ behavior: 'smooth', block: 'center' });
          email.focus({ preventScroll: true });
        }
      }
      return;
    }
    const opener = event.target.closest('[data-crosight-promotion-open]');
    const dialog = getDialog();
    if (opener) {
      controller.tick();
      if (dialog && !opener.closest('[data-crosight-promotion-badge]')?.hidden && !dialog.open) dialog.showModal();
      return;
    }
    if (dialog && event.target === dialog) dialog.close();
  });

  document.addEventListener('keydown', event => {
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-crosight-promotion-open][role="button"]')) { event.preventDefault(); event.target.click(); }
  });

  document.addEventListener('variant:change', event => {
    const section = event.target.closest('.shopify-section');
    const variant = event.detail?.variant;
    section?.querySelectorAll('[data-crosight-product-countdown], [data-crosight-promotion-badge], [data-crosight-inline-countdown]').forEach(element => {
      const stock = element.closest('[data-crosight-promotion-stock-ids]')?.dataset.crosightPromotionStockIds || '';
      element.dataset.crosightVariantAvailable = String(variant?.available === true && stock.split(',').includes(String(variant.id)));
    });
    controller.tick();
  });
  document.addEventListener('product:rerender', () => setTimeout(() => controller.tick(), 0), true);
  document.addEventListener('shopify:section:load', () => controller.tick());
  document.addEventListener('visibilitychange', () => controller.tick());
  controller.tick();
  if (document.body.dataset.crosightPromotionTimers === 'true' || document.body.dataset.crosightBadgeRule === 'schedule') window.setInterval(() => controller.tick(), 1000);
  window.crosightCountdownCore = controller;
})();



